import { Check, Copy, KeyRound } from "lucide-react";
import * as React from "react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/primitives";
import { toast } from "@/components/ui/toaster";

export interface Credential {
  email: string;
  password: string;
}

/** Shows a one-time temporary password with a copy button and a clear
 *  "shown once" warning. The value never comes back from the API again. */
export function CredentialDialog({
  credential,
  onClose,
}: {
  credential: Credential | null;
  onClose: () => void;
}) {
  const [copied, setCopied] = React.useState(false);

  async function copy() {
    if (!credential) return;
    try {
      await navigator.clipboard.writeText(credential.password);
      setCopied(true);
      toast.success("Password copied");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Copy failed — select and copy manually");
    }
  }

  return (
    <Dialog open={credential !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-[hsl(var(--accent))]" />
            Temporary password
          </DialogTitle>
          <DialogDescription>
            Share this with <span className="font-medium text-foreground">{credential?.email}</span>
            . They must change it on first sign-in.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-md border border-warning/40 bg-[hsl(var(--warning))]/10 p-3">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-[hsl(var(--warning))]">
            Shown once — copy it now
          </p>
          <div className="flex items-center gap-2">
            <code className="flex-1 select-all rounded bg-muted px-3 py-2 font-mono text-sm">
              {credential?.password}
            </code>
            <Button size="icon" variant="outline" aria-label="Copy password" onClick={copy}>
              {copied ? (
                <Check className="h-4 w-4 text-[hsl(var(--success))]" />
              ) : (
                <Copy className="h-4 w-4" />
              )}
            </Button>
          </div>
        </div>

        <DialogFooter>
          <Button onClick={onClose}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
